import type * as React from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlanInformationModalCloseIcon from "assets/icons/plan-information-modal-close.svg";
import GiftMembershipStepAccessIcon from "assets/images/gift-membership-step-access.svg";
import GiftMembershipStepEnvelopeIcon from "assets/images/gift-membership-step-envelope.svg";
import GiftMembershipStepPencilIcon from "assets/images/gift-membership-step-pencil.svg";
import GLButton from "components/v3/sharedComponents/GLButton";
import { GIFT_MAX_PLAN_QUANTITY } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import { COLORS } from "components/v3/Theme/colors";
import MembershipInviteHowItWorksBlock from "./MembershipInviteHowItWorksBlock";
import type { MembershipInviteHowItWorksStep } from "./MembershipInviteHowItWorksBlock";

const unpaidInviteWelcomeLinkLabel = `genomiclife.com${PATHS.ONBOARDING_WELCOME_UNPAID_INVITE}`;

const strongInlineTextSx = {
  color: "inherit",
  fontWeight: 700
} as const;

const sentModalSteps: ReadonlyArray<MembershipInviteHowItWorksStep> = [
  {
    icon: GiftMembershipStepEnvelopeIcon,
    title: "Wait for the email",
    body: (
      <>
        They will receive an email with their personal invite code.{" "}
        <Box component="span" sx={{ fontStyle: "italic" }}>
          It can take up to 1 hour for this code to arrive to their inbox.
        </Box>
      </>
    )
  },
  {
    icon: GiftMembershipStepPencilIcon,
    title: "Sign up with the code",
    body: (
      <>
        They&apos;ll need to create an account to use the code you sent them.
        Codes expire within 30 days.
        <br />
        <Box component="span" sx={{ fontStyle: "italic" }}>
          They can also go directly to{" "}
          <Box component="span" sx={strongInlineTextSx}>
            {unpaidInviteWelcomeLinkLabel}
          </Box>{" "}
          to get started.
        </Box>
      </>
    )
  },
  {
    icon: GiftMembershipStepAccessIcon,
    title: "They become a member",
    body: "After purchasing a plan, they'll get full access to Genomic Life."
  }
];

type ShareGenomicLifeInviteSentModalProps = {
  inviteLimitReached?: boolean;
  inviteeFirstName: string;
  onExit: () => void;
  onSendAnother: () => void;
  open: boolean;
};

const ShareGenomicLifeInviteSentModal: React.FC<
  ShareGenomicLifeInviteSentModalProps
> = ({
  open,
  inviteLimitReached = false,
  inviteeFirstName,
  onExit,
  onSendAnother
}) => {
  return (
    <Modal
      open={open}
      onClose={onExit}
      closeAfterTransition
      aria-describedby="invite-sent-modal-description"
      aria-labelledby="invite-sent-modal-title"
      slotProps={{
        backdrop: {
          sx: {
            WebkitBackdropFilter: "blur(12px)",
            backdropFilter: "blur(12px)",
            backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.52)
          }
        }
      }}
    >
      <Box
        sx={{
          left: "50%",
          maxWidth: "750px",
          position: "absolute",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 32px)"
        }}
      >
        <Box
          sx={{
            bgcolor: COLORS.WHITE,
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
            outline: 0,
            position: "relative",
            p: { sm: 4, xs: 3 },
            pt: { sm: 5, xs: 4 },
            pr: { sm: 5, xs: 4 }
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={onExit}
            sx={{
              bgcolor: COLORS.WHITE,
              border: `1px solid ${COLORS.PRIMARY_LIGHT}`,
              boxShadow: "none",
              position: "absolute",
              right: { sm: -25, xs: 10 },
              top: { sm: -25, xs: 10 },
              "&:hover": {
                bgcolor: COLORS.LIGHT_GRAY,
                boxShadow: "none"
              }
            }}
          >
            <Box
              alt=""
              aria-hidden
              component="img"
              src={PlanInformationModalCloseIcon}
              sx={{ display: "block", height: 28, width: 28 }}
            />
          </IconButton>

          <Typography
            component="h2"
            id="invite-sent-modal-title"
            variant="h4_black"
            sx={{ color: COLORS.BLACK, mb: 1.5, pr: { xs: 5, sm: 0 } }}
          >
            Thank you!
          </Typography>
          <Typography
            id="invite-sent-modal-description"
            variant="body"
            sx={{ color: COLORS.DARK_GRAY_2, lineHeight: 1.6, mb: 5 }}
          >
            We&apos;ve sent {inviteeFirstName} an email with their invite code
            and instructions on how to sign up.
          </Typography>

          <MembershipInviteHowItWorksBlock steps={sentModalSteps} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { sm: "row", xs: "column" },
              gap: 2,
              justifyContent: "flex-start",
              mt: 6
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: { sm: "auto", xs: "100%" }
              }}
            >
              <GLButton
                buttonType="primary"
                disabled={inviteLimitReached}
                onClick={onSendAnother}
                rightIcon={<ArrowForward />}
                text="Send another invite"
                sx={{
                  maxWidth: { sm: "none", xs: "100%" },
                  width: { sm: "auto", xs: "100%" }
                }}
              />
              {inviteLimitReached ? (
                <Typography
                  sx={{ color: COLORS.NOTICE, fontStyle: "italic" }}
                  variant="body2"
                >
                  Maximum of {GIFT_MAX_PLAN_QUANTITY}.
                </Typography>
              ) : null}
            </Box>
            <GLButton
              buttonType="secondary"
              onClick={onExit}
              text="Exit"
              sx={{
                maxWidth: { sm: "none", xs: "100%" },
                width: { sm: "auto", xs: "100%" }
              }}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ShareGenomicLifeInviteSentModal;
